import { useState, useCallback } from 'react';
import { AiReportCustomerProfile } from '../models/AiReportCustomerProfile';
import aiReportCustomerProfileService, { AiReportCustomerProfileInput } from '../services/aiReportCustomerProfileService';

export const useAiReportCustomerProfiles = () => {
    const [profiles, setProfiles] = useState<AiReportCustomerProfile[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchProfiles = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await aiReportCustomerProfileService.getAll();
            setProfiles(data);
        } catch (err) {
            setError('Failed to load customer profiles');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchProfileById = async (id: number) => {
        setLoading(true);
        setError(null);
        try {
            return await aiReportCustomerProfileService.getById(id);
        } catch (err) {
            setError(`Failed to load customer profile ${id}`);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const addProfile = async (data: AiReportCustomerProfileInput) => {
        const created = await aiReportCustomerProfileService.create(data);
        setProfiles((prev) => [...prev, created]);
        return created;
    };

    const updateProfile = async (id: number, data: AiReportCustomerProfileInput) => {
        const updated = await aiReportCustomerProfileService.update(id, data);
        setProfiles((prev) => prev.map((p) => (p.id === id ? updated : p)));
        return updated;
    };

    const deleteProfile = async (id: number) => {
        await aiReportCustomerProfileService.remove(id);
        setProfiles((prev) => prev.filter((p) => p.id !== id));
    };

    const importProfiles = async (file: File) => {
        const result = await aiReportCustomerProfileService.bulkImport(file);
        await fetchProfiles();
        return result;
    };

    return {
        profiles,
        loading,
        error,
        fetchProfiles,
        fetchProfileById,
        addProfile,
        updateProfile,
        deleteProfile,
        importProfiles,
    };
};
